/**
 * AdminPage - Admin Dashboard with Tabs
 *
 * Main admin page with tabs for different template management sections.
 * Responsive: uses dropdown on mobile, horizontal tabs on desktop.
 */

import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplatesPage } from './TemplatesPage';
import { PaymentMethodTemplatesPage } from './PaymentMethodTemplatesPage';
import SmtpConfigTab from './SmtpConfigTab';
import PaypalConfigTab from './PaypalConfigTab';
import SecuritySettingsTab from './SecuritySettingsTab';
import UsersTab from './UsersTab';
import AppConfigTab from './AppConfigTab';
import LegalPagesTab from './LegalPagesTab';
import FinancesTab from './FinancesTab';
import AIConfigTab from './AIConfigTab';

type TabId = 'users' | 'finances' | 'categories' | 'payment-methods' | 'smtp' | 'paypal' | 'security' | 'ai' | 'config' | 'legal-pages';

interface Tab {
  id: TabId;
  labelKey: string;
  defaultLabel: string;
  icon: React.ReactNode;
}

const FolderIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
  </svg>
);

const BanknotesIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
  </svg>
);

const CreditCardIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
  </svg>
);

const MailIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
  </svg>
);

const ShieldIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
  </svg>
);

const CogIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const DocumentIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const PaypalIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M7.5 21H4.5C4.1 21 3.8 20.7 3.8 20.3L6 4.7C6.1 4.3 6.4 4 6.8 4H13.3C15.9 4 17.8 4.6 18.8 5.8C19.7 6.9 19.9 8.3 19.5 10C19.4 10.5 19.3 10.9 19.1 11.3C18.3 14.1 16.1 15.5 13 15.5H10.5C10.1 15.5 9.8 15.8 9.7 16.2L8.5 20.3C8.4 20.7 8.1 21 7.5 21Z"/>
  </svg>
);

const AIIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
  </svg>
);

const UsersIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19.128a9.38 9.38 0 002.625.372 9.337 9.337 0 004.121-.952 4.125 4.125 0 00-7.533-2.493M15 19.128v-.003c0-1.113-.285-2.16-.786-3.07M15 19.128v.106A12.318 12.318 0 018.624 21c-2.331 0-4.512-.645-6.374-1.766l-.001-.109a6.375 6.375 0 0111.964-3.07M12 6.375a3.375 3.375 0 11-6.75 0 3.375 3.375 0 016.75 0zm8.25 2.25a2.625 2.625 0 11-5.25 0 2.625 2.625 0 015.25 0z" />
  </svg>
);

const ChevronDownIcon = ({ className = "w-5 h-5" }: { className?: string }) => (
  <svg className={className} fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
  </svg>
);

const tabs: Tab[] = [
  {
    id: 'users',
    labelKey: 'admin.tabs.users',
    defaultLabel: 'Utilisateurs',
    icon: <UsersIcon />,
  },
  {
    id: 'finances',
    labelKey: 'admin.tabs.finances',
    defaultLabel: 'Finances',
    icon: <BanknotesIcon />,
  },
  {
    id: 'categories',
    labelKey: 'admin.tabs.categories',
    defaultLabel: 'Catégories',
    icon: <FolderIcon />,
  },
  {
    id: 'payment-methods',
    labelKey: 'admin.tabs.paymentMethods',
    defaultLabel: 'Modes de paiement',
    icon: <CreditCardIcon />,
  },
  {
    id: 'smtp',
    labelKey: 'admin.tabs.smtp',
    defaultLabel: 'Configuration SMTP',
    icon: <MailIcon />,
  },
  {
    id: 'paypal',
    labelKey: 'admin.tabs.paypal',
    defaultLabel: 'PayPal',
    icon: <PaypalIcon />,
  },
  {
    id: 'security',
    labelKey: 'admin.tabs.security',
    defaultLabel: 'Sécurité',
    icon: <ShieldIcon />,
  },
  {
    id: 'ai',
    labelKey: 'admin.tabs.ai',
    defaultLabel: 'Intelligence Artificielle',
    icon: <AIIcon />,
  },
  {
    id: 'config',
    labelKey: 'admin.tabs.config',
    defaultLabel: 'Configuration',
    icon: <CogIcon />,
  },
  {
    id: 'legal-pages',
    labelKey: 'admin.tabs.legalPages',
    defaultLabel: 'Pages légales',
    icon: <DocumentIcon />,
  },
];

export function AdminPage() {
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState<TabId>('users');
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMobileMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const activeTabData = tabs.find(tab => tab.id === activeTab);

  const handleTabSelect = (tabId: TabId) => {
    setActiveTab(tabId);
    setMobileMenuOpen(false);
  };

  return (
    <div className="space-y-4 md:space-y-6">
      {/* Mobile: Dropdown menu */}
      <div className="md:hidden" ref={menuRef}>
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="w-full flex items-center justify-between px-4 py-3 bg-white border border-slate-200 rounded-lg shadow-sm"
        >
          <div className="flex items-center gap-3">
            <span className="text-violet-600">{activeTabData?.icon}</span>
            <span className="font-medium text-slate-900">
              {activeTabData && t(activeTabData.labelKey, activeTabData.defaultLabel)}
            </span>
          </div>
          <ChevronDownIcon className={`w-5 h-5 text-slate-400 transition-transform ${mobileMenuOpen ? 'rotate-180' : ''}`} />
        </button>
        
        {mobileMenuOpen && (
          <div className="absolute left-4 right-4 mt-2 bg-white border border-slate-200 rounded-lg shadow-lg z-50 overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => handleTabSelect(tab.id)}
                className={`
                  w-full flex items-center gap-3 px-4 py-3 text-left transition-colors
                  ${activeTab === tab.id
                    ? 'bg-violet-50 text-violet-600'
                    : 'text-slate-700 hover:bg-slate-50'
                  }
                `}
              >
                {tab.icon}
                <span className="font-medium">{t(tab.labelKey, tab.defaultLabel)}</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Desktop: Horizontal tabs */}
      <div className="hidden md:block border-b border-slate-200">
        <nav className="-mb-px flex space-x-8 overflow-x-auto">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`
                flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors whitespace-nowrap
                ${activeTab === tab.id
                  ? 'border-violet-500 text-violet-600'
                  : 'border-transparent text-slate-500 hover:text-slate-700 hover:border-slate-300'
                }
              `}
            >
              {tab.icon}
              {t(tab.labelKey, tab.defaultLabel)}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'finances' && <FinancesTab />}
        {activeTab === 'categories' && <TemplatesPage />}
        {activeTab === 'payment-methods' && <PaymentMethodTemplatesPage />}
        {activeTab === 'smtp' && <SmtpConfigTab />}
        {activeTab === 'paypal' && <PaypalConfigTab />}
        {activeTab === 'security' && <SecuritySettingsTab />}
        {activeTab === 'ai' && <AIConfigTab />}
        {activeTab === 'config' && <AppConfigTab />}
        {activeTab === 'legal-pages' && <LegalPagesTab />}
      </div>
    </div>
  );
}

export default AdminPage;
